// ONE ROW PER FAILURE KIND — the shape every family file (`./provider.ts`, `./box.ts`,
// `./tree.ts`, `./run.ts`) declares its kinds in, the helpers that spell a row's
// producer and budget, and the vocabularies a row is written in. They live HERE, not
// beside the projections that read them (`../failure-producer.ts`,
// `../failure-budget.ts`, `../../babysit/policy.ts`), because those files import the
// rows: a family reading its constants back from them would be an import cycle, and a
// cycle over `const`s is a TDZ throw at module load. The old paths re-export each one.

import { z } from 'zod';
import type { TaskFailureBudget, TaskRependHold } from '../failure-budget';
import type { TaskFailureProducer } from '../failure-producer';

/**
 * The cap a RETRYABLE box-fault budget gets (arc 101 WK-3, re-scoped by slice
 * 11). `failed` used to be terminal on the FIRST box error, so every transient
 * runner fault (clone fail, adapter timeout, OOM, model 500) killed the task
 * permanently — prod carried 848 failed vs 891 completed at the time this
 * landed. Three is the `agent.run` row of `LANE_JOB_OPTIONS` (`@developerz/queue`) for the same
 * reason: every attempt may run a full BYOK session, so a deterministic failure
 * must terminate, not re-bill the customer forever.
 *
 * IT IS NO LONGER THE ONLY NUMBER. Slice 11 replaced one cap for every cause
 * with {@link TASK_FAILURE_BUDGET} — a budget per {@link TaskFailureKind},
 * because a provider rate limit inside the box-fault bucket was an ACCOUNT
 * condition being charged to the task: an account-level limit burned both retry
 * attempts of task after task in ninety seconds and blocked with a message that
 * never named the cause. This constant is what the kinds that genuinely bound
 * *"the fix didn't work"* are sized at; the kinds that bound *"no fix was
 * produced"* are not charged at all.
 */
export const TASK_MAX_ATTEMPTS = 3;

/**
 * How a failure kind is BUDGETED (arc 101 slice 11). Three arms, and the
 * discriminant is the question every consumer actually asks: *does this failure
 * spend the task's attempt budget at all?*
 *
 *   `charged`   — the task's own work failed. Spend one attempt; at
 *                 `maxAttempts` the failure is terminal.
 *   `uncharged` — the failure was not the task's (an ACCOUNT condition). Spend
 *                 nothing and re-enter the queue. There is no ladder to exhaust,
 *                 so there is no exhaustion action to name — which is why this
 *                 arm carries neither field rather than carrying an ignored one.
 *                 It MAY carry `maxConsecutive` (#3094): a bound on refusals in
 *                 a row, for a kind whose re-entry can loop with nothing charged.
 *   `terminal`  — never retried, at any count. A retry cannot help by
 *                 construction, not by judgement.
 *
 * A flat `{maxAttempts, charged, onExhaustion}` record would have made
 * `maxAttempts` meaningless on the uncharged arm and `charged` meaningless on
 * the terminal one — two fields that can never be read, which is how the next
 * reader ends up setting one and expecting it to do something.
 */
export const TaskBudgetKind = {
  Charged: 'charged',
  Uncharged: 'uncharged',
  Terminal: 'terminal',
} as const;
export type TaskBudgetKind = (typeof TaskBudgetKind)[keyof typeof TaskBudgetKind];

/**
 * What a `charged` budget does when its ladder is spent.
 *
 *   `fail`   — the task ends, and so does the objective it carried.
 *   `hold`   — the task does NOT end (2026-09-29). Every attempt died on an
 *              environment fault that never GRADED the change — by the kind's own
 *              contract "no evidence about the change" — so a spent ladder is a fact
 *              about the fleet, not the work. The failure write re-pends the row HELD
 *              on `env_fault_exhausted` with a blocker naming the refusal and the
 *              release (`task redispatch`), so its dependents and its plan wait on it
 *              instead of cascade-failing. `taskFailureHoldsOnExhaustion`
 *              (`../failure-budget.ts`) is the predicate; which kinds carry it, and
 *              why each other box-fault kind does not, is enumerated there.
 *
 * THE EVIDENCE for `hold`: prod, 30 days to 2026-09-29, 158 tasks ended `failed
 * gate_unrunnable` (all at attempt >= 3), 148 of them with no error of any other kind
 * across every run, and 13 more cascade-failed through `depends_on` from 5 of them.
 * The measured one, `tsk_1be837ae…`, refused on `cargo-deny not found` — a tool missing
 * from every box — and six dependents died unclaimed.
 */
export const TaskExhaustionAction = { Fail: 'fail', Hold: 'hold' } as const;
export type TaskExhaustionAction = (typeof TaskExhaustionAction)[keyof typeof TaskExhaustionAction];

/**
 * WHO WRITES a {@link TaskFailureKind}. Three arms, and the discriminant is the
 * question nobody could answer for `git_auth`: *can this value ever appear?*
 *
 *   `wire`            — a box stamps `err.code = '<the kind>'` at a NAMED throw
 *                       site; `loop.ts` forwards it unchanged and
 *                       {@link classifyTaskFailure} grades it by enumeration.
 *   `wire_unproduced` — in the wire vocabulary and graded exactly the same, but
 *                       NOTHING in the tree stamps it today. The kind is a
 *                       contract waiting for a writer, and saying so is the
 *                       whole point: an unreachable kind is otherwise
 *                       indistinguishable from one that never happens.
 *   `platform`        — a mothership lane writes the kind straight to
 *                       `tasks.failure_kind`. No box, no wire code.
 *
 * The two wire arms are ONE budgeting and grading behaviour, split only by
 * whether a producer exists. That is deliberate: demoting an unproduced kind out
 * of the wire vocabulary would mean the producer that lands tomorrow silently
 * grades as `runner_error`, which is the failure this table exists to end rather
 * than a second copy of it.
 */
export const TaskFailureSeam = {
  Wire: 'wire',
  WireUnproduced: 'wire_unproduced',
  Platform: 'platform',
} as const;
export const TaskFailureSeamSchema = z.enum(TaskFailureSeam);
export type TaskFailureSeam = z.infer<typeof TaskFailureSeamSchema>;

/**
 * Does a babysit round that ended with a failure kind spend its LADDER's budget? The
 * vocabulary of a row's `babysitRound`; the rule that picks between the two members is
 * documented on `BABYSIT_ROUND_CHARGE` (`../../babysit/policy.ts`).
 */
export const BabysitRoundCharge = { Charged: 'charged', Uncharged: 'uncharged' } as const;
export type BabysitRoundCharge = (typeof BabysitRoundCharge)[keyof typeof BabysitRoundCharge];

/**
 * How long a review parked on `TaskHoldReason.ReviewAwaitingModel` waits before the
 * dispatcher re-asks the pool (plan 101 K4). The BACKSTOP, not the wake: a key
 * transition to `valid` releases the account's parked reviews at once (the
 * `billing.key_verified` drain). This clock exists for the key changes that fire no
 * signal — a lane designation cleared, a model un-parked by a capture, a
 * `credits_exhausted` key promoted by the reverify sweep — so none of them can
 * strand a review for good.
 *
 * Six hours, and the number is the trade it names: each lapse costs one claim, one
 * rollback and one `dispatch.no_credential` row per parked review. Measured
 * 2026-09-21, the worst account filed 74 blind reviews in 24h; at 6h that is ~300
 * rows a day while it stays keyless, against ~1,800 at a 1h clock. Not a cost bound
 * on the customer — nothing is billed while parked.
 */
export const REVIEW_NO_MODEL_RECHECK_MS = 6 * 60 * 60 * 1000;

/**
 * How long a task parked on `TaskHoldReason.NoScreenKey` waits before the claim may
 * re-decide it (plan 101 09-10). The signal that ends the wait — a key on the account
 * turning `valid` — releases it at once (`releaseHoldsOnKeyValid`); this is the backstop
 * for the changes that fire none (a designation cleared, a model un-parked). The same
 * trade as {@link REVIEW_NO_MODEL_RECHECK_MS}, for the same reason: each lapse costs one
 * claim and one rollback per parked task, and nothing is billed while it waits.
 */
export const NO_SCREEN_KEY_RECHECK_MS = REVIEW_NO_MODEL_RECHECK_MS;

/**
 * How long a run that ended `screen_unavailable` (parked on `TaskHoldReason.ProviderCapacity`)
 * or `screen_key_unusable` (on `TaskHoldReason.NoScreenKey`) waits before it may be claimed
 * again (2026-10-09). THE FAILURE IT PREVENTS (`docs/standards/agent-budgets.md`): re-pended
 * at once, a task whose screen cannot answer — the account's key on a spent 5-hour usage
 * window — was re-claimed every 1-2 s, lease epoch 1 -> 19 in 40 minutes across 5 boxes,
 * each claim a box and a screen call spent on the same dead key. On the keyless park a key
 * turning `valid` releases it at once (`releaseHoldsOnKeyValid`); this is the clock for a
 * screen that failed for a reason no key event clears (a timeout, a fenced request). Fifteen minutes, the capacity
 * park's backstop and for its reason: nothing is billed while parked, and a screen failure
 * is usually a provider wall that lifts within the hour, not the six-hour no-key case.
 */
export const SCREEN_UNAVAILABLE_RECHECK_MS = 15 * 60 * 1000;

/**
 * How long a task parked on `TaskHoldReason.ProviderKeyRefused` waits before it may be
 * claimed again (plan 101 L3). This is the BACKSTOP, not the wake. A key on the account
 * turning `valid` releases the parked rows at once. This clock covers the key changes
 * that fire no signal: a box's own backend key replaced, or a provider's weekly window
 * resetting on a key nobody touched.
 *
 * Six hours, the review re-check's number, sized to the same trade. It is a separate
 * constant because a lapse here costs more than a lapse there: a claim, a checkout and
 * a run up to the first refused call, where a review lapse costs only a rolled-back
 * claim. That makes about four probes a day per parked task, never a hot loop. It is not
 * a cost bound on the customer: a refused call bills nothing.
 */
export const PROVIDER_KEY_REFUSED_RECHECK_MS = 6 * 60 * 60 * 1000;

/**
 * How long a task re-pended HELD on `TaskHoldReason.ProviderCapacity` waits before it
 * may be claimed again — the BACKSTOP for a `provider_rate_limit` refusal that quoted
 * no reset (or one `trustedQuotaReset` refuses to trust). When the refusal DID state a
 * trusted recovery window, the park ends THERE instead, exactly as the key park does.
 *
 * Fifteen minutes, not the key park's six hours: a capacity refusal bills nothing and
 * costs one claim, but the pool's quoted resets are minutes long (~6 min measured on
 * prod 2026-09-20/21), so a six-hour backstop would strand work behind a wall that
 * lifted an hour ago. The stated reset is the wake; this clock covers the refusals
 * that carry no parsable one.
 */
export const PROVIDER_CAPACITY_RECHECK_MS = 15 * 60 * 1000;

/**
 * The FIRST window of an undated `provider_transient` park — one consecutive uncharged
 * failure. Each further one in a row doubles it (`FailureKindPark.backoffCapMs`).
 *
 * THE FAILURE IT PREVENTS (#6501, 2026-10-11..18): the transient re-pend was claimable at
 * once by every box but the one that graded it (that box's own pull hold is the only pacer
 * it had, `apps/runner/src/backend-cooldown.ts`), so on a six-box account five tasks re-ran
 * 1,699 times in a week, one every ~50 s, at `uncharged_streak` up to 384 — each run a
 * claim, a checkout, a `bin/setup` and a refused model call against a provider that had
 * said not before a date. A minute is the box hold's own order of magnitude: the first
 * re-try after an outage costs one claim more than today, never a lost task.
 */
export const PROVIDER_TRANSIENT_BACKOFF_BASE_MS = 60 * 1000;

/**
 * The CEILING of that backoff: an outage of any length is re-probed at least hourly, so a
 * provider that recovered is noticed within the hour. Not a count that ends work — the kind
 * stays uncharged and unbounded; this bounds only how stale "is it back yet" may get.
 */
export const PROVIDER_TRANSIENT_BACKOFF_CAP_MS = 60 * 60 * 1000;

/**
 * The FIRST window of a `box_baseline_red` park (F7, 2026-10-19): the repo's verify failed
 * on the base with the diff removed, so nothing was graded. Each further refusal in a row
 * doubles it up to {@link BOX_BASELINE_RED_BACKOFF_CAP_MS}.
 *
 * THE FAILURE IT PREVENTS: the cap it replaced. `uncharged(3)` ended the task after three
 * refusals in a row, so a red default branch failed every task it touched: 26 tesote.com
 * tasks `failed box_baseline_red` on 2026-10-05..07. Unparked and uncapped instead, every
 * box the exclusion does not cover re-claims the row at once, and each refusal costs a whole
 * agent run (the base probe runs after the diff's gate). Thirty minutes is the refusing
 * box's own exclusion (`BOX_FAULT_EXCLUSION_MINUTES`), so the first retry is open to it too.
 */
export const BOX_BASELINE_RED_BACKOFF_BASE_MS = 30 * 60 * 1000;

/**
 * The CEILING of that backoff: a day. The WAKE is a push to the default branch, which
 * releases the park (the worker's default-branch push release), so this clock is only the
 * fallback for a base fixed without a push the mothership sees. Each lapse is a claim, a
 * checkout and a full paid run up to the base probe, so a base that stays red costs one
 * such run a day per parked task once the backoff tops out (30 min doubling: 30m, 1h, 2h,
 * 4h, 8h, 16h, 24h).
 */
export const BOX_BASELINE_RED_BACKOFF_CAP_MS = 24 * 60 * 60 * 1000;

/**
 * Where a re-pend of this kind PARKS instead of re-entering the queue at once, or `null`
 * for a kind that never parks. Read by `taskFailureRependHold` (`../failure-budget.ts`).
 */
export type FailureKindPark = {
  readonly reason: TaskRependHold['reason'];
  /** The flat hold when no trusted stated reset ends the park sooner; the FIRST window when it backs off. */
  readonly backstopMs: number;
  /** Does a provider's stated reset (`StatedKeyReset`), when trusted, end the park instead of `backstopMs`? */
  readonly honorsStatedReset: boolean;
  /**
   * Present ⇒ an UNSTATED park backs off: `backstopMs` doubles per consecutive uncharged
   * failure (`tasks.uncharged_streak`, which only the failure write knows) up to this
   * ceiling. The write computes it with `@developerz/kit`'s `exponentialBackoffMs`.
   */
  readonly backoffCapMs?: number;
};

/**
 * What a reader is told about a failure of this kind, per surface. Each string is the
 * WHOLE sentence for that surface — no surface appends to it.
 */
export type FailureKindCopy = {
  /** The worker's task alert (`apps/worker/src/fleet-alerts/task-lifecycle.ts`), before its ladder phrase. */
  readonly alert: string;
  /**
   * The api's failure sentence for a row at `attempt = 0`. For a charged kind that means
   * no box ever claimed it; for an UNCHARGED kind (`PoolKeySpent`, `ReviewNoModel`) a
   * failure spends no attempt, so a row that ran can still read 0 — the field name is
   * kept for the api wire, and the sentence must hold for both readings.
   */
  readonly unclaimed: string;
  /** The api's failure sentence for a row that ran (`attempt ≥ 1`). */
  readonly ran: string;
};

/**
 * EVERYTHING the platform decides about one failure kind, in one place. Adding a kind is
 * one of these in one family file: every table the platform reads per kind is projected
 * from the rows (`projectFailureKinds`, `../failure-kind.ts`), so a kind missing a field
 * fails typecheck at its row and a kind cannot be half-added.
 *
 * Retryability is NOT a field: it is `budget.kind !== 'terminal'` (`taskFailureRetryable`),
 * derived so the two can never disagree.
 */
export type FailureKindRow = {
  /** The stored `tasks.failure_kind` value — and, for a `wire` producer, the wire `code`. */
  readonly value: string;
  /**
   * This kind's position in `TaskFailureKindSchema.options` — the order the MCP catalog
   * and the JSON schemas render the enum in, so a public contract. APPEND-ONLY: a new
   * kind takes the next unused ordinal (`n` for `n` existing kinds), and an existing
   * row's ordinal never changes. `./registry.test.ts` holds them unique and contiguous
   * from 0, and pins the shipped order.
   */
  readonly ordinal: number;
  readonly producer: TaskFailureProducer;
  readonly budget: TaskFailureBudget;
  readonly park: FailureKindPark | null;
  /** Must the re-pend exclude the box that produced it (`taskFailureBoxLocal`)? */
  readonly boxLocal: boolean;
  /** Would an operator go inspect THE BOX — the `box_unhealthy` gate (`taskFailureBlamesBox`)? */
  readonly blamesBox: boolean;
  readonly babysitRound: BabysitRoundCharge;
  /** Does an exhausted ladder with saved work park and resume (`RESUME_PARKABLE_KINDS`)? */
  readonly resumeParkable: boolean;
  /** Is it kept out of the fleet failure-rate numerator (`taskFailureRateExcluded`)? */
  readonly failureRateExcluded: boolean;
  readonly copy: FailureKindCopy;
};

/** A box stamps the code at each named throw site (`path#Symbol`). */
export const wire = (...producedBy: string[]): TaskFailureProducer => ({
  seam: TaskFailureSeam.Wire,
  producedBy,
});

/** In the wire vocabulary, and nothing stamps it today — `why` says how that was established. */
export const wireUnproduced = (why: string): TaskFailureProducer => ({
  seam: TaskFailureSeam.WireUnproduced,
  why,
});

/** A mothership lane writes the column directly (`path#Symbol` per lane). */
export const platform = (...producedBy: string[]): TaskFailureProducer => ({
  seam: TaskFailureSeam.Platform,
  producedBy,
});

/** The task's own work failed: spend one of `maxAttempts`, then `onExhaustion`. */
export const charged = (
  maxAttempts: number,
  onExhaustion: TaskExhaustionAction,
): TaskFailureBudget => ({ kind: TaskBudgetKind.Charged, maxAttempts, onExhaustion });

/** Not the task's failure: spend nothing; `maxConsecutive` bounds refusals in a row, when stated. */
export const uncharged = (maxConsecutive?: number): TaskFailureBudget =>
  maxConsecutive === undefined
    ? { kind: TaskBudgetKind.Uncharged }
    : { kind: TaskBudgetKind.Uncharged, maxConsecutive };

/**
 * An outage of the ACCOUNT's keys: spend nothing, cap nothing, and leave the run of
 * refusals as it was (`streakNeutral`, `../failure-budget.ts`). The park is the bound.
 */
export const unchargedOutage = (): TaskFailureBudget => ({
  kind: TaskBudgetKind.Uncharged,
  streakNeutral: true,
});

/** Never retried, at any count. */
export const terminal = (): TaskFailureBudget => ({ kind: TaskBudgetKind.Terminal });
